import MultiVendorSelector from "@/components/MultiVendorSelector";
import GraphLoader from "@/components/Nivo/GraphLoader";
import GraphRow from "@/components/Nivo/GraphRow";
import PageContent from "@/components/PageContent";
import PageHeader from "@/components/PageHeader";
import Container from "@/components/Ui/Container";
import { useVendorContext } from "@/contexts/vendor-context";
import Router from "next/router";
import { useEffect, useState } from "react";

const GraphsHolder = ({
  selectedVendors,
  vendorsParam,
  currency,
}: {
  selectedVendors: string[];
  vendorsParam: string;
  currency: string;
}) => {
  if (selectedVendors.length === 0) {
    return (
      <Container className="space-y-2 p-8 text-center">
        <div className="text-xl! font-medium">No vendor selected</div>
        <div className="text-muted-foreground">
          Please select at least one of your vendors to see the multi vendor
          analytics
        </div>
      </Container>
    );
  } else {
    // Build extra query parameters from vendorsParam and currency
    const buildExtraParams = (
      includesCurrency: boolean = false,
    ): [string, string][] => {
      const params: [string, string][] = [];

      // Parse vendors parameter from vendorsParam string
      if (
        vendorsParam.startsWith("?vendors=") &&
        vendorsParam !== "?vendors=_"
      ) {
        params.push(["vendors", vendorsParam.replace("?vendors=", "")]);
      } else if (vendorsParam.startsWith("?excludedVendors=")) {
        params.push([
          "excludedVendors",
          vendorsParam.replace("?excludedVendors=", ""),
        ]);
      }

      // Add currency if needed and provided
      if (includesCurrency && currency) {
        params.push(["currency", currency]);
      }

      return params;
    };

    return (
      <>
        <GraphRow className="[--aspect:5/1]">
          <GraphLoader
            endPoint="graphs/multiVendors/totalSales"
            extraQueryParams={buildExtraParams(true)}
          />
          <GraphLoader
            endPoint="graphs/multiVendors/users"
            extraQueryParams={buildExtraParams()}
          />
        </GraphRow>
        <GraphRow className="[--aspect:5/1]">
          <GraphLoader
            endPoint="graphs/multiVendors/subscriptions"
            extraQueryParams={buildExtraParams()}
          />
        </GraphRow>
        <GraphRow className="[--aspect:5/1]">
          <GraphLoader
            endPoint="graphs/multiVendors/singlePurchases"
            extraQueryParams={buildExtraParams()}
          />
          <GraphLoader
            endPoint="graphs/multiVendors/nNewSubscriptions"
            extraQueryParams={buildExtraParams()}
          />
          <GraphLoader
            endPoint="graphs/multiVendors/nEndedSubscriptions"
            extraQueryParams={buildExtraParams()}
          />
          {/* <GraphLoader
            endPoint="graphs/multiVendors/subscribers"
            extraQueryParams={buildExtraParams()}
          /> */}
        </GraphRow>
        <GraphRow className="[--aspect:5/1]">
          <GraphLoader
            endPoint="graphs/multiVendors/mrr"
            extraQueryParams={buildExtraParams(true)}
          />
        </GraphRow>
      </>
    );
  }
};

const processVendors = (
  selectedVendors: string[],
  availableVendors: string[],
) => {
  if (selectedVendors.length === 0) {
    return "?vendors=_"; // return a string that will not match any vendor
  }
  if (selectedVendors.length === availableVendors.length) {
    return "";
  }
  if (selectedVendors.length <= availableVendors.length / 2) {
    return `?vendors=${selectedVendors.join(",")}`;
  }
  if (selectedVendors.length > availableVendors.length / 2) {
    return `?excludedVendors=${availableVendors.filter((v) => !selectedVendors.includes(v)).join(",")}`;
  }
  return "";
};

const Index = () => {
  const { selectedVendor, vendors, multiVendorKey, getVendorSettings } =
    useVendorContext();
  const [isLoadingVendors, setIsLoadingVendors] = useState(true);
  const [selectedVendors, setVendors] = useState<string[]>([]);
  const [currencyString, setCurrencyString] = useState<string>("");
  const multiVendorSettings = getVendorSettings(multiVendorKey);

  useEffect(() => {
    if (multiVendorKey !== selectedVendor) {
      Router.push(`/${selectedVendor}/analytics`);
    }
    // Only initialize selectedVendors on first load, not on SWR revalidation
    if (isLoadingVendors && selectedVendor && vendors && vendors.length > 0) {
      setVendors(vendors.filter((v) => v !== multiVendorKey));
      setIsLoadingVendors(false);
    }
  }, [multiVendorKey, selectedVendor, vendors, isLoadingVendors]);

  if (isLoadingVendors) {
    return null;
  }

  const vendorsParam = processVendors(selectedVendors, vendors || []);

  return (
    <>
      <div className="pb-3 pl-4">
        <PageHeader
          headline={multiVendorSettings?.name || ""}
          showDatePicker
          hideGlobalSearch={true}
        />
        <PageContent>
          <MultiVendorSelector
            selectedVendors={selectedVendors}
            setSelectedVendors={setVendors}
            selectedCurrency={currencyString}
            setSelectedCurrency={setCurrencyString}
          />
          <GraphsHolder
            selectedVendors={selectedVendors}
            vendorsParam={vendorsParam}
            currency={currencyString}
          />
        </PageContent>
      </div>
    </>
  );
};

export default Index;
